import { Pressable, Text, TextInput, View } from 'react-native';

import { ChipMultiRow } from '../primitives/ChipMultiRow';
import type { UiTheme } from '../theme';
import type { ExercisePickerProps } from './pickerProps';
import type { PickerStyles } from './pickerStyles';
import { stateAria } from "../a11y";

// The picker's commit bar. Extracted from ExercisePicker (PE-40) — that file
// was at 493 and the Workout tab does not fit under the cap beside it.
//
// ⛔ A pure move: same two branches, same disabled rules, same copy. The
// Superset branch carries the group's optional name because the name is part of
// the same commit — asking for it anywhere else would let a coach name a group
// they then fail to create.
// #336: the Add branch can carry «In which days» (the program editor's
// `addDays`), the chips above the button; without it the branch is unchanged.
export function PickerFooter({
  theme,
  styles,
  t,
  variant,
  nameOverride,
  setNameOverride,
  supersetTotal,
  commitSuperset,
  selectedCount,
  commitAdd,
  days,
  selectedDays,
  onToggleDay,
}: {
  theme: UiTheme;
  styles: PickerStyles;
  t: (key: string, opts?: Record<string, unknown>) => string;
  variant: 'add' | 'superset';
  nameOverride: string;
  setNameOverride: (v: string) => void;
  supersetTotal: number;
  commitSuperset: () => void;
  selectedCount: number;
  commitAdd: () => void;
  days?: ExercisePickerProps['addDays'];
  selectedDays?: ReadonlySet<number>;
  onToggleDay?: (day: number) => void;
}) {
  if (variant === 'superset') {
    const blocked = supersetTotal < 2;
    return (
      <View style={styles.footer}>
        <TextInput
          style={styles.nameInput}
          placeholder={t('mobile.superset.namePlaceholder', {
            defaultValue: 'Superset name (optional)',
          })}
          placeholderTextColor={theme.palette.ink3}
          value={nameOverride}
          onChangeText={setNameOverride}
          maxLength={60}
          returnKeyType="done"
        />
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ disabled: blocked }}
          {...stateAria({ disabled: blocked })}
          disabled={blocked}
          onPress={commitSuperset}
          style={[styles.footerBtn, blocked && styles.footerBtnDisabled]}
        >
          <Text style={[styles.footerBtnText, blocked && styles.footerBtnTextDisabled]}>
            {t('mobile.superset.createCta', {
              n: supersetTotal,
              defaultValue: 'Create superset ({{n}})',
            })}
          </Text>
        </Pressable>
      </View>
    );
  }

  const dayCount = selectedDays?.size ?? 0;
  const blocked = selectedCount === 0 || (days !== undefined && dayCount === 0);
  return (
    <View style={styles.footer}>
      {days && selectedDays && onToggleDay ? (
        <View style={styles.footerDays}>
          <ChipMultiRow
            theme={theme}
            choices={days.labels.map((_, i) => i)}
            selected={selectedDays}
            onToggle={onToggleDay}
            label={(i) => days.labels[i] ?? ''}
            chipA11yLabel={(i) => days.a11yLabels[i] ?? days.labels[i] ?? ''}
            groupA11yLabel={t('mobile.logger.pickerInDays', { defaultValue: 'In which days' })}
            title={t('mobile.logger.pickerInDays', { defaultValue: 'In which days' })}
          />
        </View>
      ) : null}
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: blocked }}
        {...stateAria({ disabled: blocked })}
        disabled={blocked}
        onPress={commitAdd}
        style={[styles.footerBtn, blocked && styles.footerBtnDisabled]}
      >
        <Text style={[styles.footerBtnText, blocked && styles.footerBtnTextDisabled]}>
          {days !== undefined && dayCount > 1
            ? t('mobile.logger.pickerAddNInDays', {
                n: selectedCount,
                count: dayCount,
                defaultValue: 'Add {{n}} in {{count}} days',
              })
            : t('mobile.logger.pickerAddN', {
                n: selectedCount,
                defaultValue: 'Add {{n}}',
              })}
        </Text>
      </Pressable>
    </View>
  );
}
